import React, {FC} from 'react';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {isTeamNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {AnalyticsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {getHasAccessToAnyKnowledgeBase} from '../../../store/data/knowledgeBases/knowledgeBasesSelectors';
import {AnalyticsReportsEnum} from '../../helpers/reportHelpers/reportTypes';
import {StyledAnalyticsNavigationTitleDiv} from './legacyReportNavigationLinks';
import {ReportNavigationLink} from './reportNavigationLink';

/*
 * Constants.
 */

const interactionId = 'analytics-automation-reports';
const interactionProps = buildInteractionIdProps(interactionId);

const automationReportsLinks = [
  {
    type: AnalyticsReportsEnum.CHATBOTS,
    name: <Trans id="analytics-chatbots-automation-reports">Chatbots</Trans>,
    iconName: 'chatbotSmall',
    selectedIconName: 'chatbotFilledSmall',
    featureStatus: undefined,
  },
  {
    type: AnalyticsReportsEnum.KNOWLEDGE_BASE,
    name: <Trans id="analytics-knowledge-base-automation-reports">Knowledge base</Trans>,
    iconName: 'knowledgeBaseSmall',
    selectedIconName: 'knowledgeBaseFilledSmall',
    featureStatus: undefined,
  },
] as const;

/*
 * Props.
 */

interface ReportNavigationAutomationReportsLinksProps {
  panelId: AnalyticsPanelId;
  isDisabled: boolean;
  namespace: string;
}

/*
 * Styles.
 */

const StyledNavigationHelpWrapper = styled.div`
  display: flex;
  flex-direction: column;

  margin-top: 20px;

  &:first-child {
    margin-top: 4px;
  }
`;

/*
 * Component.
 */

export const ReportNavigationAutomationReportsLinks: FC<ReportNavigationAutomationReportsLinksProps> = ({
  panelId,
  isDisabled,
  namespace,
}) => {
  const hasAccessToAnyKnowledgeBase = useAppSelector(getHasAccessToAnyKnowledgeBase);

  const isChatbotReportVisible = isTeamNamespace(namespace);
  if (!isChatbotReportVisible && !hasAccessToAnyKnowledgeBase) {
    return null;
  }

  return (
    <StyledNavigationHelpWrapper {...interactionProps}>
      <StyledAnalyticsNavigationTitleDiv>
        <Trans id="analytics-self-service-navigation-title">Self-service</Trans>
      </StyledAnalyticsNavigationTitleDiv>
      {automationReportsLinks
        .filter((link) => {
          switch (link.type) {
            case AnalyticsReportsEnum.CHATBOTS:
              return isChatbotReportVisible;
            case AnalyticsReportsEnum.KNOWLEDGE_BASE:
              return hasAccessToAnyKnowledgeBase;
            default:
              return true;
          }
        })
        .map((link) => (
          <ReportNavigationLink
            panelId={panelId}
            key={link.type}
            reportType={link.type}
            iconName={link.iconName}
            selectedIconName={link.selectedIconName}
            isDisabled={isDisabled}
            featureStatus={link.featureStatus}
          >
            {link.name}
          </ReportNavigationLink>
        ))}
    </StyledNavigationHelpWrapper>
  );
};
